<template>
    <div
        v-for="(field, colIdx) in primaryFields"
        :key="field.key"
        :style="field.style || {}"
        :data-row-index="index"
        :data-col-index="colIdx"
        :data-col-id="field.key"
        class="cr-tr-grid-element"
        @keydown.ctrl.shift="(ev) => handleKeydown(ev, {rowIndex: index, columnIndex: colIdx, columnId: field.key})"
    >
        <component
            :is="field.component"
            v-if="field.component"
            v-model="computedLine"
            :data-row-index="index"
            :data-col-index="colIdx"
            :data-col-id="field.key"
            @move-to-next="() => onRequestMoveToNext(index, field)"
            @show-more-inputs="(show: boolean) => showMoreInputs = show"
        />
        <div v-else>
            No component for {{ field.key }}
        </div>
    </div>
    <div class="flex items-center gap-1 justify-end mr-1 line-actions">
        <UButton
            variant="subtle"
            size="sm"
            class="px-1"
            color="neutral"
            title="Vis flere felt"
            tabindex="-1"
            :data-cy="`${TransactionManagerCy.ButtonShowMoreIndexed}-${index}`"
            @click="showMoreInputs = !showMoreInputs"
        >
            <i class="block text-md i-ph-caret-right transition-all" :class="{'rotate-90': showMoreInputs}"/>
        </UButton>
        <UButton
            variant="subtle"
            size="sm"
            class="px-1"
            color="neutral"
            title="Kopier linje (Ctrl-Shift-*)"
            tabindex="-1"
            :data-cy="`${TransactionManagerCy.ButtonDuplicateLineIndexed}-${index}`"
            @click="emit('handle-copy-line', line, index)"
        >
            <i class="block text-md i-ph-copy"/>
        </UButton>
        <UButton
            variant="subtle"
            size="sm"
            class="px-1"
            color="neutral"
            title="Ny linje (Ctrl-Shift-+)"
            :data-cy="`${TransactionManagerCy.ButtonAddLineIndexed}-${index}`"
            @click="emit('handle-new-blank-line', index)"
        >
            <i class="block text-md i-ph-plus"/>
        </UButton>
        <UButton
            v-if="transactionEntryLines.length > 1"
            variant="subtle"
            size="sm"
            class="px-1"
            color="error"
            :data-cy="`${TransactionManagerCy.ButtonDeleteLineIndexed}-${index}`"
            title="Slett linje (Ctrl-Shift--)"
            tabindex="-1"
            @click="emit('handle-delete-line-at-index', index)"
        >
            <i class="block text-md i-ph-x"/>
        </UButton>
        <UButton
            v-else
            variant="subtle"
            size="sm"
            class="px-1"
            color="error"
            :data-cy="`${TransactionManagerCy.ButtonDeleteLineIndexed}-${index}`"
            title="Nullstill linje (Ctrl-Shift--)"
            tabindex="-1"
            @click="emit('handle-delete-line-at-index', index)"
        >
            <i class="block text-md i-ph-prohibit"/>
        </UButton>
    </div>

    <div v-if="showMoreInputs" class="col-span-full col-start-2 pb-2" :data-cy="`row-${index}`">
        <div class="flex flex-grow-1 gap-1">
            <template
                v-for="field in expandedFields"
                :key="field.key"
            >
                <div :style="field.style">
                    <div class="text-xs whitespace-nowrap truncate"> {{ field.label }}</div>
                    <component
                        :is="field.component"
                        v-if="field.component"
                        v-model="computedLine"
                    />
                    <div v-else>No component for {{ field.key }}</div>
                </div>
            </template>
        </div>
    </div>
</template>

<script lang="ts" setup>

import {computed, nextTick, onMounted, PropType, ref, watch} from 'vue'
import {TransactionEntryLine} from '@/types/transaction/TransactionEntryLine'
import {lastSelectedDate, useTransactionCreator} from '@/transaction/create-transaction/TransactionCreator.service'
import DatePicker from '@/dateUtils/DatePicker.vue'
import VatPicker from '@/vat/VatPicker.vue'
import CurrencyAmountInput from '@/components/CurrencyAmountInput.vue'
import _ from 'lodash'
import {LINE_COLUMN} from '@/transaction/create-transaction/EntryLineInstance'
import {MaybeElement, useFocus} from '@vueuse/core'
import useDynamicTransactionFields, {TransactionInputField} from '@/transaction/create-transaction/transaction-fields.controller'
import {handleKeydown} from '@/transaction/create-transaction/KeyboardOperations.service'
import {useSetting} from '@/setting/setting.service'
import {TransactionManagerCy} from '@/transaction/TransactionManagerCy'
import AccountPicker from '@/account-picker/AccountPicker.vue'

const dateSelector = ref<typeof DatePicker | null>(null)
const debitPicker = ref<typeof AccountPicker | null>(null)
const debitVatPicker = ref<typeof VatPicker | null>(null)
const creditPicker = ref<typeof AccountPicker | null>(null)
const creditVatPicker = ref<typeof VatPicker | null>(null)
const amountInput = ref<typeof CurrencyAmountInput | null>(null)
const accrualDurationInputRef = ref<typeof HTMLInputElement | null>(null)

const showMoreInputs = ref(false)

const {transactionEntryLines} = useTransactionCreator()
const {primaryFields, expandedFields, getNextFieldKey} = useDynamicTransactionFields()

const {setting: projectRequired} = useSetting<boolean | null>('PROJECT_REQUIRED')
const {setting: departmentRequired} = useSetting<boolean | null>('DEPARTMENT_REQUIRED')

onMounted(() => {
    if (projectRequired.value.value === true || departmentRequired.value.value === true) {
        showMoreInputs.value = true
    }
})

watch(() => departmentRequired.value, () => {
    if (projectRequired.value.value === true || departmentRequired.value.value === true) {
        showMoreInputs.value = true
    }
})

const emit = defineEmits([
    'update', 'new-line', 'new-line-beneath',
    'copy', 'delete', 'enter-on-last', 'error',
    'handle-copy-line', 'handle-new-blank-line', 'handle-delete-line-at-index'
])

const props = defineProps({
    index: {
        type: Number,
        required: true
    },
    line: {
        type: Object as PropType<TransactionEntryLine>,
        required: true
    }
})

const debitVatDisabled = computed(() => {
    return !!props.line.debitSubAccount || props.line.debitAccount?.startsWith('27')
})

const creditVatDisabled = computed(() => {
    return !!props.line.creditSubAccount || props.line.creditAccount?.startsWith('27')
})

const computedLine = computed({
        get: () => props.line,
        set: (val: TransactionEntryLine) => {
            Object.assign(props.line, val)
        }
    }
)

const focusDate = () => {
    nextTick(() => {
        dateSelector.value?.focus()
    })
}

const focusDebit = () => {
    nextTick(() => {
        debitPicker.value?.focus()
    })
}

const focusDebitVat = () => {
    if (debitVatDisabled.value) {
        nextTick(() => {
            creditPicker.value?.focus()
        })
    } else {
        nextTick(() => {
            debitVatPicker.value?.focus()
        })
    }
}

const focusCredit = () => {
    nextTick(() => {
        creditPicker.value?.focus()
    })
}

const focusCreditVat = () => {
    if (creditVatDisabled.value) {
        nextTick(() => {
            amountInput.value?.focus()
        })
    } else {
        nextTick(() => {
            creditVatPicker.value?.focus()
        })
    }
}

const focusAmount = () => {
    nextTick(() => {
        amountInput.value?.focus()
    })
}

const focusAccrualDuration = () => {
    nextTick(() => {
        // TODO: Fix correct typing for ref
        // @ts-ignore
        accrualDurationInputRef.value?.focus()
    })
}

const dimensionsInputRef = ref<MaybeElement>()

const focusDimensions = () => {
    const {focused} = useFocus(dimensionsInputRef)
    focused.value = true
}

const columns = [
    LINE_COLUMN.Date,
    LINE_COLUMN.DebitAccount,
    LINE_COLUMN.DebitAccountVAT,
    LINE_COLUMN.CreditAccount,
    LINE_COLUMN.CreditAccountVAT,
    LINE_COLUMN.Amount,
    LINE_COLUMN.Dimensions
]

function findNewColumnId(columnId: string, offset: number) {
    const activeElementIndex = _.indexOf(columns, columnId)
    let newIndex = activeElementIndex + offset

    if (newIndex < 0) {
        newIndex = 0
    }
    if (newIndex >= columns.length) {
        newIndex = columns.length - 1
    }
    return columns[newIndex]
}

function callFocusFunction(colId: string) {
    switch (colId) {
        case LINE_COLUMN.Date: {
            focusDate()
            break
        }
        case LINE_COLUMN.AccrualAccount: {
            focusAccrualDuration()
            break
        }
        case LINE_COLUMN.DebitAccount: {
            focusDebit()
            break
        }
        case LINE_COLUMN.DebitAccountVAT: {
            focusDebitVat()
            break
        }
        case LINE_COLUMN.CreditAccount: {
            focusCredit()
            break
        }
        case LINE_COLUMN.CreditAccountVAT: {
            focusCreditVat()
            break
        }
        case LINE_COLUMN.Amount: {
            focusAmount()
            break
        }
        case LINE_COLUMN.Dimensions: {
            focusDimensions()
            break
        }
    }
}

const moveFocusToColumn = (currentColumn: LINE_COLUMN, columnOffset: number) => {
    const newColumnId = findNewColumnId(currentColumn, columnOffset)
    if (!newColumnId) {
        console.error('Something went wrong, could not find next colId')
        return
    }
    callFocusFunction(newColumnId)
}

const onRequestMoveToNext = (index: number, currentField: TransactionInputField) => {
    const nextField = getNextFieldKey(currentField.key)

    const selector = `[data-row-index="${index}"][data-col-id="${nextField}"] input`
    const elem = document.querySelector(selector)
    if (!elem || !(elem as HTMLInputElement).focus) {
        return
    }
    (elem as HTMLInputElement).focus()
}

const resetAllPickers = () => {
    debitPicker?.value?.reset()
    debitVatPicker?.value?.reset()
    creditPicker?.value?.reset()
    creditVatPicker?.value?.reset()
}

defineExpose({
    moveFocusToColumn,
    lastSelectedDate,
    resetAllPickers
})


</script>

<style scoped>
@reference "@/index.css";
@reference "tailwindcss";

.tooltip {
    @apply bg-base-100 rounded p-2 absolute right-0 shadow-2xl w-28;
    z-index: 9999;
}

.line-actions {
    i {
        @apply w-[18px];
    }
}

</style>